import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, X, Send } from "lucide-react";

const CATEGORIES = [
  { key: "scheda", label: "Scheda/Esercizi" },
  { key: "prenotazioni", label: "Prenotazioni" },
  { key: "pagamenti", label: "Pagamenti" },
  { key: "generale", label: "Nota generale" },
];

export default function ChatWidget({ role }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [category, setCategory] = useState("generale");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (role !== "client") return;
    try { const { data } = await api.get("/chat/messages"); setMessages(data); } catch {}
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [role]);

  // When the client opens the widget, mark every admin reply as read and clear related notifications.
  useEffect(() => {
    if (open && role === "client") {
      (async () => {
        try { await api.post("/chat/messages/read-all"); }
        catch (_) {}
        load();
        // Trigger a global refresh of the bell (best-effort — listener in AppShell picks it up on next poll/mount).
        window.dispatchEvent(new Event("notifications:refresh"));
      })();
    }
    // eslint-disable-next-line
  }, [open]);

  if (!role || role === "admin") return null; // admin uses dedicated page

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/chat/messages", { category, text });
      toast.success("Messaggio inviato a Sara");
      setText(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const unreadReplies = messages.filter(m => m.admin_reply && !m.read_by_client).length;

  return (
    <>
      <button
        data-testid="chat-widget-btn"
        onClick={() => setOpen(!open)}
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 w-14 h-14 bg-[#A855F7] hover:bg-[#9333EA] text-white flex items-center justify-center shadow-lg z-40 transition-colors"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && unreadReplies > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold flex items-center justify-center bg-[#FF3366] text-white">
            {unreadReplies}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="chat-widget-panel" className="fixed bottom-20 md:bottom-24 right-4 md:right-6 w-[92vw] max-w-md bg-[#120B19] border-4 border-white z-40 flex flex-col h-[70vh] max-h-[560px] overflow-hidden shadow-2xl">
          <div className="p-4 border-b border-white/10">
            <div className="font-heading uppercase tracking-tight">Scrivi a Sara</div>
            <div className="text-[11px] text-white/50 mt-1">Ti risponderà appena possibile.</div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
            {messages.length === 0 && <div className="text-white/40 text-sm text-center py-8">Nessun messaggio ancora.</div>}
            {messages.slice().reverse().map(m => (
              <div key={m.id} className="space-y-1">
                <div className="ml-auto bg-[#A855F7]/15 border border-[#A855F7]/30 p-2 text-sm text-white max-w-[85%] ml-auto">
                  <div className="text-[9px] uppercase tracking-widest text-[#A855F7] mb-1">{m.category}</div>
                  {m.text}
                  <div className="text-[9px] text-white/40 mt-1">{new Date(m.created_at).toLocaleString("it-IT")}</div>
                </div>
                {m.admin_reply && (
                  <div className="bg-white/5 border border-white/10 p-2 text-sm max-w-[85%]">
                    <div className="text-[9px] uppercase tracking-widest text-white/50 mb-1">Sara</div>
                    {m.admin_reply}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-white/10 space-y-2">
            <select data-testid="chat-category" value={category} onChange={e => setCategory(e.target.value)}
              className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none">
              {CATEGORIES.map(c => <option key={c.key} value={c.key} className="bg-[#120B19]">{c.label}</option>)}
            </select>
            <div className="flex gap-2">
              <textarea rows={2} data-testid="chat-text" value={text} onChange={e => setText(e.target.value)} placeholder="Scrivi qui…"
                className="flex-1 bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none resize-none" />
              <button data-testid="chat-send" disabled={busy || !text.trim()} onClick={send} className="btn-primary !px-3">
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
